/*
 * Purpose: Single mission request field — registry-driven renderer (incl. vault)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 92
 */
import { useState } from 'react';
import { Lock, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import type { MissionKnowledgeField } from '@/services/mission-knowledge/types';
import { VAULT_FIELD_MARKER } from '@/services/mission-knowledge/types';
import { vaultKeyForField, vaultSecretConfigured } from '@/lib/missionRequestFields';
import { useMissionKnowledgeStore } from '@/stores/missionKnowledgeStore';
import { useSecretStore } from '@/stores/secretStore';
import { VaultUnlockInline } from '@/components/vault/VaultUnlockInline';
import { cn } from '@/lib/utils';

interface MissionRequestFieldProps {
  field: MissionKnowledgeField;
  requestId: string;
  className?: string;
}

export function MissionRequestField({ field, requestId, className }: MissionRequestFieldProps) {
  const structuredAnswers = useMissionKnowledgeStore((s) => s.structuredAnswers);
  const setStructuredAnswer = useMissionKnowledgeStore((s) => s.setStructuredAnswer);
  const isUnlocked = useSecretStore((s) => s.isUnlocked);
  const setSecret = useSecretStore((s) => s.setSecret);
  const [vaultDraft, setVaultDraft] = useState('');

  const inputId = `mk-field-${requestId}-${field.id}`;
  const value = structuredAnswers[field.id] ?? '';

  if (field.type === 'info') {
    return (
      <div
        className={cn(
          'rounded-md border border-border/50 bg-muted/40 px-3 py-2 text-xs text-muted-foreground',
          className
        )}
      >
        {field.label && <p className="font-medium text-foreground mb-1">{field.label}</p>}
        <p>{field.body || field.helpText}</p>
      </div>
    );
  }

  if (field.type === 'vault_secret') {
    const vaultKey = vaultKeyForField(field);
    const configured = vaultSecretConfigured(vaultKey);

    async function saveToVault() {
      if (!vaultDraft.trim()) {
        toast.error('Enter a value first');
        return;
      }
      try {
        await setSecret(vaultKey, vaultDraft.trim());
        setStructuredAnswer(field.id, `${VAULT_FIELD_MARKER}${vaultKey}`);
        setVaultDraft('');
        toast.success(`Saved to vault as ${vaultKey}`);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Vault save failed', {
          action: {
            label: 'Unlock vault',
            onClick: () => document.getElementById('vault-inline-pass')?.focus(),
          },
        });
      }
    }

    return (
      <div className={cn('space-y-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3', className)}>
        <div className="flex flex-wrap items-center gap-2">
          <Lock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" aria-hidden />
          <Label htmlFor={inputId} className="text-xs">
            {field.label}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          {configured && (
            <Badge variant="secondary" className="text-[10px]">
              In vault
            </Badge>
          )}
        </div>
        {field.helpText && <p className="text-[10px] text-muted-foreground">{field.helpText}</p>}
        <p className="text-[10px] text-muted-foreground font-mono">{vaultKey}</p>
        {!isUnlocked && <VaultUnlockInline compact />}
        {isUnlocked && !configured && (
          <>
            <Input
              id={inputId}
              type="password"
              autoComplete="off"
              value={vaultDraft}
              onChange={(e) => setVaultDraft(e.target.value)}
              placeholder={field.placeholder ?? 'Sensitive value — stored encrypted'}
              className="h-9 text-sm"
            />
            <Button type="button" size="sm" variant="secondary" onClick={() => void saveToVault()}>
              <Save className="h-3.5 w-3.5 mr-1" />
              Save to vault
            </Button>
          </>
        )}
        {configured && (
          <p className="text-[11px] text-muted-foreground">
            Value encrypted on this device — not sent to mission RAG or logs.
          </p>
        )}
      </div>
    );
  }

  const labelEl = (
    <>
      <Label htmlFor={inputId} className="text-xs">
        {field.label}
        {field.required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      {field.helpText && <p className="text-[10px] text-muted-foreground mt-0.5">{field.helpText}</p>}
    </>
  );

  if (field.type === 'textarea' || field.type === 'file_text') {
    return (
      <div className={className}>
        {labelEl}
        <Textarea
          id={inputId}
          value={value}
          onChange={(e) => setStructuredAnswer(field.id, e.target.value)}
          placeholder={
            field.placeholder ?? (field.type === 'file_text' ? 'Paste document content…' : undefined)
          }
          rows={field.type === 'file_text' ? 6 : 3}
          className={cn('mt-1 text-sm', field.type === 'file_text' && 'font-mono text-[13px]')}
        />
      </div>
    );
  }

  if (field.type === 'boolean') {
    return (
      <div
        className={cn(
          'flex items-center justify-between gap-3 rounded-md border border-border/50 px-3 py-2',
          className
        )}
      >
        <div className="min-w-0">
          <Label htmlFor={inputId} className="text-xs">
            {field.label}
          </Label>
          {field.helpText && <p className="text-[10px] text-muted-foreground">{field.helpText}</p>}
        </div>
        <Switch
          id={inputId}
          checked={value === 'true'}
          onCheckedChange={(checked) => setStructuredAnswer(field.id, checked ? 'true' : 'false')}
        />
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div className={className}>
        {labelEl}
        <select
          id={inputId}
          className="mt-1 w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={value}
          onChange={(e) => setStructuredAnswer(field.id, e.target.value)}
        >
          <option value="">Choose…</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === 'multiselect') {
    let selected: string[] = [];
    try {
      selected = JSON.parse(value || '[]') as string[];
    } catch {
      selected = value ? value.split(',').map((s) => s.trim()) : [];
    }
    const toggle = (opt: string) => {
      const next = selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt];
      setStructuredAnswer(field.id, JSON.stringify(next));
    };
    return (
      <div className={className}>
        {labelEl}
        <div className="mt-2 flex flex-wrap gap-2">
          {(field.options ?? []).map((opt) => (
            <label key={opt} className="flex items-center gap-1.5 text-xs cursor-pointer">
              <Checkbox checked={selected.includes(opt)} onCheckedChange={() => toggle(opt)} />
              {opt}
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === 'rating') {
    const max = field.maxRating ?? 5;
    const n = Number(value) || 0;
    return (
      <div className={className}>
        {labelEl}
        <div className="mt-2 flex flex-wrap gap-1">
          {Array.from({ length: max }, (_, i) => i + 1).map((star) => (
            <Button
              key={star}
              type="button"
              size="sm"
              variant={n >= star ? 'default' : 'outline'}
              className="h-8 w-8 p-0 text-xs"
              onClick={() => setStructuredAnswer(field.id, String(star))}
            >
              {star}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === 'tags') {
    return (
      <div className={className}>
        {labelEl}
        <Input
          id={inputId}
          value={value}
          onChange={(e) => setStructuredAnswer(field.id, e.target.value)}
          placeholder={field.placeholder ?? 'Comma-separated tags'}
          className="mt-1 h-9 text-sm"
        />
      </div>
    );
  }

  const inputType =
    field.type === 'number'
      ? 'number'
      : field.type === 'email'
        ? 'email'
        : field.type === 'url'
          ? 'url'
          : field.type === 'date'
            ? 'date'
            : field.type === 'phone'
              ? 'tel'
              : 'text';

  return (
    <div className={className}>
      {labelEl}
      <Input
        id={inputId}
        type={inputType}
        value={value}
        onChange={(e) => setStructuredAnswer(field.id, e.target.value)}
        placeholder={field.placeholder}
        className="mt-1 h-9 text-sm"
      />
    </div>
  );
}
